import React, { forwardRef, useEffect, useMemo } from 'react';
import { CircularProgress, FormControl, FormHelperText, InputLabel, SxProps, Theme } from '@mui/material';
import ReactSelect, { MultiValue, SingleValue } from 'react-select';
import useIssuedManuals from 'views/App/Portal/Policy/IssuedManuals/_data_sources/useIssuedManuals';
import { Manual } from 'views/App/Portal/Policy/_types/manuals/draft';
import { isEmpty } from 'lodash';

interface ManualOption {
  value: string;
  label: string;
  manual: Manual;
}

export type ManualPickerProps = {
  allowBlank?: boolean;
  allowMultiple?: boolean;
  id: string;
  label?: string;
  value?: string[];
  onChange: (value: string[]) => void;
  organizationId: string;
  jurisdictionId?: string;
  globalOnly?: boolean;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  error?: any;
  helperText?: string | undefined;
  size?: string;
  sx?: SxProps<Theme>;
};

const ManualPicker = forwardRef(
  (
    {
      allowBlank = false,
      allowMultiple = false,
      id,
      label,
      value = [],
      onChange,
      organizationId,
      jurisdictionId,
      globalOnly = false,
      placeholder,
      disabled = false,
      required,
      error,
      helperText,
      size,
      sx,
    }: ManualPickerProps,
    ref
  ) => {
    const manualsQuery = useIssuedManuals(organizationId, { size: 100 });
    const manualOptions = useMemo(() => {
      try {
        const allManuals = manualsQuery.data?.pages?.flatMap((page) => page.content) ?? [];
        let filteredManuals = allManuals;
        if (globalOnly) {
          filteredManuals = allManuals.filter((manual) => isEmpty(manual.jurisdiction?.id));
        } else if (!isEmpty(jurisdictionId)) {
          filteredManuals = allManuals.filter((manual) => manual.jurisdiction?.id === jurisdictionId);
        }

        return [
          ...(allowBlank ? [{ value: '', label: placeholder ?? '-- All Manuals --' }] : []),
          ...filteredManuals.map((x) => ({
            value: x.id!,
            label: x.name,
            manual: x,
          })),
        ];
      } catch (e) {
        return [];
      }
    }, [manualsQuery.data, allowBlank, jurisdictionId, globalOnly]);

    const selectedOptions = useMemo(() => {
      return manualOptions.filter((x) => (value ?? []).includes(x.value));
    }, [value, manualOptions]);

    useEffect(() => {
      if (manualsQuery.hasNextPage) {
        manualsQuery.fetchNextPage();
      }
    }, [manualsQuery.data, manualsQuery.hasNextPage]);

    return manualsQuery.data?.pages[0]?.content ? (
      <FormControl
        key={id}
        fullWidth
        error={!!error}
        sx={sx}
      >
        {!!label && <InputLabel required={required}>{label}</InputLabel>}
        <ReactSelect
          name={id}
          value={selectedOptions}
          options={manualOptions}
          placeholder={placeholder}
          menuPortalTarget={document.body}
          styles={{
            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
            menu: (base) => ({ ...base, width: 300 }),
            input: (base) => ({
              ...base,
              boxShadow: 'none',
              outline: 0,
              minHeight: size === 'small' ? 26 : 45,
            }),
          }}
          menuPosition='fixed'
          menuPlacement='auto'
          isMulti={allowMultiple}
          isSearchable
          isDisabled={disabled}
          onChange={(newValue) => {
            if (Array.isArray(newValue)) {
              const newVals = newValue as MultiValue<ManualOption>;
              onChange((newValue ?? []).map((x) => x.value));
            } else {
              const newVal = newValue as SingleValue<ManualOption>;
              onChange(!!newVal ? [newVal.value] : []);
            }
          }}
        />
        {!!error && <FormHelperText error>{error.message}</FormHelperText>}
        {!!helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    ) : (
      <CircularProgress />
    );
  }
);

export default ManualPicker;
